import type { UiCardVariant } from "@agent/UiPrimitives";
import {
  EyeIcon,
  type IconProps,
  Swipe,
  type SwipeTone,
  TriangleExclamationIcon,
} from "@scribenote/brian-design-system";
import type { ComponentType } from "react";

/**
 * Per-card status for the subject dashboard, calmest to most urgent. `urgent`
 * is the "act now" state — rare by design (alarm fatigue is the enemy).
 * WEB-3195 renders it multi-modally (surface tint + accent bar + dot + icon +
 * label — never colour alone); WEB-3196 additionally floats `urgent` cards to
 * the top of the dashboard.
 */
export type CardSeverity = "calm" | "watch" | "urgent";

/**
 * Narrow the API's plain `severity` string (BE-1918) to the card vocabulary.
 * Anything unexpected falls back to `calm` — fail safe, so a value we don't
 * recognise never paints phantom chrome.
 */
export const toCardSeverity = (value: string): CardSeverity =>
  value === "watch" || value === "urgent" ? value : "calm";

/** Ranked calmest → most urgent, so a set of severities can be reduced to its
 * worst (a room tile takes the most urgent severity across its occupants), or
 * ordered worst-first (the subject rail's index). */
const SEVERITY_RANK: Record<CardSeverity, number> = {
  calm: 0,
  watch: 1,
  urgent: 2,
};

/**
 * Most urgent first. A stable sort, so ties keep the incoming order — over an
 * already due-date-sorted list that gives severity then due date.
 */
export const byUrgencyFirst = (
  a: { severity: CardSeverity },
  b: { severity: CardSeverity },
): number => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];

/**
 * The most urgent severity across a set, or `calm` when the set is empty. Used
 * to roll several surfaced views up to one card tint - a patient's tint is the
 * max across their surfaced views, a room tile's the max across its occupants
 * (WEB-3214).
 */
export const maxSeverity = (
  severities: Iterable<CardSeverity>,
): CardSeverity => {
  let worst: CardSeverity = "calm";
  for (const severity of severities) {
    if (SEVERITY_RANK[severity] > SEVERITY_RANK[worst]) worst = severity;
  }
  return worst;
};

/**
 * Whether a card floats to the urgent zone at the top of the subject pane
 * (WEB-3196/WEB-3268) — the single source for the include/exclude partition the
 * pane splits views and tasks on, so the two halves can't drift apart.
 */
export const isUrgent = (severity: CardSeverity): boolean =>
  severity === "urgent";

/**
 * Calm is deliberately absent: a calm card is a clean card, no chrome, plain
 * paper.
 *
 * Each severity is one highlighter out of the drawer, and the pair that comes
 * with it: the wash (`tone`, worn by the status word) and the ink (everything
 * with no word to carry it: bars, edges, rings). Watch takes the flag marker,
 * urgent the stopped one; watch's ring is the one wash outside a word, since the
 * flag ink reads brown at ring width. The `/8` and `/15` fills are the ink held
 * far back, for tinting a whole surface without a bright field on it.
 */
const SEVERITY_META: Record<
  Exclude<CardSeverity, "calm">,
  {
    label: string;
    icon: ComponentType<IconProps>;
    tone: SwipeTone;
    card: UiCardVariant;
    accent: string;
    faint: string;
    subtle: string;
    border: string;
    ring: string;
    ringHover: string;
  }
> = {
  watch: {
    label: "Watch",
    icon: EyeIcon,
    tone: "flag",
    card: "warning",
    accent: "bg-ink-flag",
    faint: "bg-ink-flag/8",
    subtle: "bg-ink-flag/15",
    border: "border-ink-flag",
    // The wash, not the flag ink: the ink is AA-dark for text and reads brown at
    // ring width on cream (SubjectRail's SEVERITY_OPEN_BORDER, same call).
    ring: "ring-wash-flag",
    // Spelled out, not `hover:${ring}`: Tailwind only emits classes it finds
    // literally in the source.
    ringHover: "hover:ring-wash-flag",
  },
  urgent: {
    label: "Urgent",
    icon: TriangleExclamationIcon,
    tone: "stopped",
    card: "danger",
    accent: "bg-ink-stopped",
    faint: "bg-ink-stopped/8",
    subtle: "bg-ink-stopped/15",
    border: "border-ink-stopped",
    ring: "ring-ink-stopped",
    ringHover: "hover:ring-ink-stopped",
  },
};

/**
 * Severity as a surface rather than a card (WEB-3288), in status ink: `surface`
 * tints a whole reading pane, `subtle` a single row, `accent` is a bar, `border`
 * a left edge, `ring`/`ringHover` an outline. `label` is the status word and
 * `tone` the highlighter it wears. `null` when calm.
 *
 * Every fill here is a transparency, so it only holds its hue over an untinted
 * backdrop.
 */
export const severityReaderChrome = (
  severity: CardSeverity,
): {
  surface: string;
  subtle: string;
  accent: string;
  border: string;
  ring: string;
  ringHover: string;
  label: string;
  tone: SwipeTone;
  icon: ComponentType<IconProps>;
} | null =>
  severity === "calm"
    ? null
    : {
        surface: SEVERITY_META[severity].faint,
        subtle: SEVERITY_META[severity].subtle,
        accent: SEVERITY_META[severity].accent,
        border: SEVERITY_META[severity].border,
        ring: SEVERITY_META[severity].ring,
        ringHover: SEVERITY_META[severity].ringHover,
        label: SEVERITY_META[severity].label,
        tone: SEVERITY_META[severity].tone,
        icon: SEVERITY_META[severity].icon,
      };

/**
 * Severity as an a2ui card's own chrome, for the two hosts that hand a whole
 * tree to `UiRenderer` and so can't reach inside the card it draws: the variant
 * turns into a status-ink ring on `CardFrame`. Calm is `default` - a clean card.
 */
export const severityVariant = (severity: CardSeverity): UiCardVariant =>
  severity === "calm" ? "default" : SEVERITY_META[severity].card;

/**
 * The state said in a word, under the highlighter that means it: icon + label
 * marked with a wash, so severity never rests on colour alone. The old bare
 * status dot is gone: a wash carries a word or it doesn't appear.
 *
 * Hosted inside a card's own header where it has one (TaskCard, TaskIndexCard,
 * summary), on a row in the subject rail, or written above the card where its
 * internals are out of reach (a2ui payloads, which take the ring themselves).
 */
export const SeverityBadge = ({
  severity,
  dataTestId,
}: {
  severity: CardSeverity;
  dataTestId: string;
}) => {
  if (severity === "calm") return null;
  const meta = SEVERITY_META[severity];
  const Icon = meta.icon;
  return (
    <Swipe
      tone={meta.tone}
      icon={<Icon />}
      data-testid={dataTestId}
      className="shrink-0 text-annotation-caps"
    >
      {meta.label}
    </Swipe>
  );
};

/**
 * The agent's one-line "why" (BE-1918) shown beside the badge for
 * `watch`/`urgent` — the specific signal ("Weight down 12% in 3 months") the
 * colour stands for. Nothing when calm or when the agent gave no reason.
 */
export const SeverityReason = ({ reason }: { reason: string | null }) =>
  reason ? (
    <span className="min-w-0 text-xs text-annotation">{reason}</span>
  ) : null;
